/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useEffect, useRef } from 'react';
// utils
import {CHART_SETTINGS, formatNumber, formatYAxis, processDataForAxis} from '../../../../../../utils/charts';
// material
import { ResponsiveLine } from '@nivo/line'; // https://nivo.rocks/line/
// styles
import injectSheet from 'react-jss';
import styles from './stackablechart_styles';


/* ========== ~~~~~~~~~~ STACKABLE CHART ~~~~~~~~~~ ========== */
const StackableChart = injectSheet(styles)((props) => {

    const chartMaxY = (Math.max(...props.data[0].data.map(chartData => chartData.y), 0) * 1.9);
    const stackedChartRef = useRef(null);

    useEffect(() => {
        if(props.locked) {
            document.addEventListener('mousedown', handleOutsideClick, false);
        } 
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick, false);
        }
    }, [props.locked]);

    const handleOutsideClick = e  => {
        props.lock(false);
        props.setCurrentPosition({x: 0, y: 0, data: null});
        props.setCurrentYAxes([
            ...props.currentYAxes
        ].map(yAxes => ({...yAxes, y: 0})));
        props.onMouseLeave();
        for (const a of stackedChartRef.current.parentNode.querySelectorAll("text")) {
            a.style.fontWeight = 'normal';
            a.style.outline = 'none';
        }
    }

    const removeTimeframeOutline = (x) => {
        for (const a of stackedChartRef.current.parentNode.querySelectorAll("text")) {
            if (a.textContent.includes(x)) {
                a.style.fontWeight = 'normal';
                a.style.outline = 'none';
            }
        }
    }

    const setDots = e => {
        const currentYAxes = props.currentYAxes;
        currentYAxes[props.index] = {id: props.id, y: e.y};

        props.datasets.map((dataset, index) => {
            if(props.index !== index) {
                const maxY = Math.max(...dataset.data[0].data.map(chartData => chartData.y), 0) * 1.9;
                const y = dataset.data[0].data[e.index].y;
                if(props.id !== 'unitLoad' && index == 2 && dateLength > 10) {
                    // fix the position of unitloads dot when hovering on other metric type and when the x-axis is at 60 degress
                    if(!props.last && dateLength > 10) {
                        // fix position when the date range is more than 10 and x-axis is at 60 degrees
                        currentYAxes[index].y = 60 - ((y / maxY) * 80);
                    } else {
                        currentYAxes[index].y = 71 - ((y / maxY) * 90);
                    }
                } else {
                    currentYAxes[index].y = 71 - ((y / maxY) * 70);
                }
            }
        })

        props.setCurrentYAxes(currentYAxes);
    }

    const handleMouseMove = e => {
        if(!props.locked) {
            if(props.currentPosition.x != e.x) {
                props.setCurrentPosition({x: e.x, y:e.y, data: e.data});
                setDots(e);
                // Update the revenue drivers value in the chart table
                const dataSets = {}
                props.datasets.forEach(dataset => {
                    dataSets[dataset.id] = dataset.data[0].data[e.index].y; // loop on each chart and get the current value
                    dataSets[`${dataset.id}ChangeRate`] = dataset.data[0].data[e.index][`${dataset.id}ChangeRate`]; // loop on each chart and get the current value
                });
                props.onMouseMove(dataSets, e.data.tableDate); 
                removeTimeframeOutline(e.data.x);
            }
        }
    }

    const handleClick = e => {
        removeTimeframeOutline(e);

        props.setCurrentPosition({x: e.x, y:e.y, data: e.data});
        setDots(e);
        for (const a of stackedChartRef.current.parentNode.querySelectorAll("text")) {
            if (a.textContent.includes(e.data.x)) {
                a.style.fontWeight = 'bolder';
                // a.style.outline = '1px solid rgb(192, 201, 220)'
            }
          }
        props.lock(true);

        // Update the revenue drivers value in the chart table
        const dataSets = {}
        props.datasets.forEach(dataset => {
            dataSets[dataset.id] = dataset.data[0].data[e.index].y; // loop on each chart and get the current value
            dataSets[`${dataset.id}ChangeRate`] = dataset.data[0].data[e.index][`${dataset.id}ChangeRate`]; // loop on each chart and get the current value
        });
        props.onMouseMove(dataSets, e.data.tableDate); 
      
    }
    
    const currentData = props.currentPosition;

    const getDataLength = () => {
        let length = 0;
        props.data.forEach(item => {
            if(item.data.length > 0) length = item.data.length;
        })
        return length;
    }

    const dateLength = getDataLength(); 

    const lineProps = dateLength && props.period.toLowerCase() === 'daily' ?
        {...CHART_SETTINGS.linePropsForTimeScale(dateLength), margin: {
                "top": 5,
                "right": 80,
                "bottom": props.last ? 50 : 5,
                "left": 30
            }} :
        CHART_SETTINGS.linePropsDefualt(dateLength)

    return (
        <div ref={stackedChartRef} className={`stackablechart ${props.classes.stackablechart}`}>
            <ResponsiveLine
                {...lineProps}
                data={props.period.toLowerCase() === 'daily' ? processDataForAxis(props.data) : props.data}
                theme={{
                    crosshair: {
                        line: {
                            stroke: 'rgba(6, 43, 117, 0.5)',
                            strokeWidth: 1,
                            strokeOpacity: 0.75,
                            strokeDasharray: ''
                        }
                    },
                }}
                colors={[`${props.color}`]}
                tooltip={()=>null}
                onMouseMove={handleMouseMove}
                onClick={handleClick}
                yScale={{
                    "type": "linear",
                    "stacked": false,
                    "min": "0",
                    "max": chartMaxY && !Number.isNaN(chartMaxY) ? chartMaxY : 'auto'
                }}
                isInteractive={true}
                layers={[
                    'grid', 'markers',  'axes', 'areas', 
                    'lines', 'points', 'slices', 'mesh', 'legends', 
                    d => (<CustomLine borderColor={props.color} currentY={props.currentYAxes[props.index].y} currentPoint={currentData} />), 
                ]}
                axisLeft={null}
                yFormat={value => formatNumber(value, props.yType)}
                enableGridX={false}
                enableGridY={true}
                gridYValues={2}
                lineWidth={2}
                animate={true}
                motionStiffness={90}
                motionDamping={15}
                axisTop={null}
                axisRight={{
                    "orient": "left",
                    "tickSize": 0,
                    "tickPadding": 20,
                    "tickRotation": 0,
                    "tickValues": 2,
                    "format": value => formatYAxis(value, props.yType),
                }}
                // colors={{ scheme: 'set1' }}
                enablePoints={props.enablePoints ? props.enablePoints : false}
                enableArea={true}
                areaOpacity={1}
                crosshairType={"x"}
                useMesh={true}
                legends={[]}
                enableSlices={false}
                enableCrossHair={true}
            />
        </div>
    );
});

const CustomLine = (props) => {
    const { currentPoint, borderColor, currentY } = props;
    if (currentPoint) {
      return (
        <>
            <svg>
                <path style={{transition: 'all .5s ease'}}
                    d={`M ${currentPoint.x} 0 L ${currentPoint.x} 80`}
                    stroke={'rgba(6,43,117, 0.5)'}
                    strokeWidth={1}
                    fill="none"
                />
            </svg>
            <g>
                <circle style={{transition: 'all .5s ease'}}
                    r={4}
                    cx={currentPoint.x}
                    cy={currentY}
                    fill="#ffffff"
                    stroke={borderColor}
                    strokeWidth={2}
            />
            </g>
         </>
      );
    }
};

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default StackableChart;
